import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import FilterModal from "../filterModal";
import TableHeader from "../table-header";
import { categoryService } from "../../utils/services/categoryService";
import { toast } from "react-toastify";

// ─── Styled components ───────────────────────────────────────────────────────

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 8,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

// ─── Actions popover ─────────────────────────────────────────────────────────

const ActionPopover = ({ row, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openPopover = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);
  const doAction = (type) => {
    closePopover();
    if (type === "edit") onEdit(row);
    if (type === "delete") onDelete(row);
  };

  return (
    <Box>
      <IconButton
        size="small"
        onClick={openPopover}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box display="flex" flexDirection="column" p={1}>
          <Button
            onClick={() => doAction("edit")}
            sx={{ textTransform: "none" }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <EditIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => doAction("delete")}
            sx={{ textTransform: "none" }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <DeleteIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

// ─── Lazy-loading tree row ────────────────────────────────────────────────────

const CategoryRow = ({ node, depth, colCount, onEdit, onDelete, clientId }) => {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [hasChildren, setHasChildren] = useState(true);

  const handleToggle = async () => {
    if (!loaded) {
      setChildrenLoading(true);
      try {
        const res = await categoryService.fetchByParent(node._id, clientId);
        const kids = res?.data || [];
        setChildren(kids);
        setLoaded(true);
        setHasChildren(kids.length > 0);
        if (kids.length > 0) setExpanded(true);
      } catch (e) {
        console.error(e);
        setLoaded(true);
        setHasChildren(false);
      } finally {
        setChildrenLoading(false);
      }
    } else {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <>
      <StyledTableRow>
        <StyledTableCell>
          <Box display="flex" alignItems="center" sx={{ pl: depth * 3 }}>
            {childrenLoading ? (
              <CircularProgress size={16} sx={{ mr: 0.5 }} />
            ) : hasChildren || !loaded ? (
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ mr: 0.3, p: 0 }}
              >
                {expanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 24, mr: 0.5 }} />
            )}
            <Typography
              fontFamily="Poppins"
              fontSize={13}
              fontWeight={depth === 0 ? 600 : 400}
            >
              {node.name}
            </Typography>
          </Box>
        </StyledTableCell>

        {!clientId && (
          <StyledTableCell align="left">
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              color="text.secondary"
            >
              {depth === 0 ? node.client?.business_name || "N/A" : "—"}
            </Typography>
          </StyledTableCell>
        )}

        <StyledTableCell align="left">
          {loaded ? (
            children.length > 0 ? (
              <Chip
                label={children.length}
                size="small"
                color="primary"
                sx={{ fontFamily: "Poppins", fontSize: 11, minWidth: 28 }}
              />
            ) : (
              <Typography
                fontFamily="Poppins"
                fontSize={12}
                color="text.secondary"
              >
                0
              </Typography>
            )
          ) : (
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              color="text.secondary"
            >
              —
            </Typography>
          )}
        </StyledTableCell>

        <StyledTableCell align="right">
          <ActionPopover row={node} onEdit={onEdit} onDelete={onDelete} />
        </StyledTableCell>
      </StyledTableRow>

      {expanded &&
        children.map((child) => (
          <CategoryRow
            key={child._id}
            node={child}
            depth={depth + 1}
            colCount={colCount}
            onEdit={onEdit}
            onDelete={onDelete}
            clientId={clientId}
          />
        ))}
    </>
  );
};

// ─── Main list component ──────────────────────────────────────────────────────

const CategoryList = ({
  data,
  onEditData,
  onDeleteData,
  filterData,
  onChangeFilter,
  currentPage,
  itemsPerPage,
  clientId,
  loading
}) => {
  const theme = useTheme();
  const primary_color = theme.palette?.primary?.main || "#D13438";

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedFilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key === filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey,
    });
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await categoryService.delete(deleteTarget._id);
      toast.success("Category deleted successfully!");
      onDeleteData && onDeleteData();
    } catch (e) {
      toast.error("Failed to delete category.");
      console.error(e);
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const TableSchema = [
    {
      key: "_id",
      title: "Name",
      filterKey: "_id",
      handleOpenFilter,
    },
    ...(clientId
      ? []
      : [
          {
            key: "client",
            title: "Client",
            filterKey: "client",
            handleOpenFilter,
          },
        ]),
    {
      key: "sub_categories",
      title: "Sub Categories",
    },
    {
      key: "action",
      title: "",
      width: "5%",
    },
  ];

  const colCount = TableSchema.length;

  return (
    <>
      {deleteLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="inherit" sx={{ color: "#fff" }} />
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid var(--border-color)",
          overflowY: "auto",
          maxHeight: "100%",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ border: "1px solid var(--border-color)" }}
        >
          <TableHead>
            <StyledTableRow>
              {TableSchema.map((column) => (
                <TableHeader
                  key={column.key}
                  column={column}
                  filterData={filterData}
                  filterKey={column.filterKey}
                  handleOpenFilter={column.handleOpenFilter}
                  title={column.title}
                />
              ))}
            </StyledTableRow>
          </TableHead>

          <TableBody>
            { loading ? (
              [...Array(10)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {[...Array(colCount)].map((_, colIndex) => (
                    <TableCell key={colIndex} sx={{ p: 2 }}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : !data || data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={13}
                    color="text.secondary"
                  >
                    No categories found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((node) => (
                <CategoryRow
                  key={node._id}
                  node={node}
                  depth={0}
                  colCount={colCount}
                  onEdit={onEditData}
                  onDelete={(row) => setDeleteTarget(row)}
                  clientId={clientId}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedFilterData?.options || []}
          selectedValues={selectedFilterData?.filter || []}
          onSave={handleSaveFilter}
          currentSortOrder={selectedFilterData?.sort}
          filterKey={selectedFilterData?.key}
          hasMore={selectedFilterData?.loadMoreButton}
          displayKey={selectedFilterData?.displayKey}
          id={selectedFilterData?.id}
          fetchApi={selectedFilterData?.fetchApi}
          searchKey={selectedFilterData?.searchKey}
        />
      </Popover>

      <DeleteConfirmationModal
        open={Boolean(deleteTarget)}
        handleClose={() => setDeleteTarget(null)}
        handleDelete={handleDelete}
        title={`Delete category "${deleteTarget?.name}"?`}
      />
    </>
  );
};

export default CategoryList;
